import type { BBoxPixels } from "@/types/annotations";

export type MeasurementHudCorner = "top-right" | "top-left" | "bottom-right" | "bottom-left";

interface MeasurementHudPlacementInput {
  imageWidth: number;
  imageHeight: number;
  bbox?: BBoxPixels;
  pointer: { x: number; y: number };
  topRightUnavailable: boolean;
}

const CORNERS: Record<MeasurementHudCorner, { x: number; y: number }> = {
  "top-right": { x: 0.9, y: 0.1 },
  "top-left": { x: 0.1, y: 0.1 },
  "bottom-right": { x: 0.9, y: 0.9 },
  "bottom-left": { x: 0.1, y: 0.9 },
};

export const MEASUREMENT_HUD_CORNER_CLASSES: Record<MeasurementHudCorner, string> = {
  "top-right": "right-3 top-3",
  "top-left": "left-3 top-3",
  "bottom-right": "bottom-3 right-3",
  "bottom-left": "bottom-3 left-3",
};

function scoreCorner(
  corner: MeasurementHudCorner,
  avoidPoints: { x: number; y: number }[],
  width: number,
  height: number
): number {
  const anchor = CORNERS[corner];
  return avoidPoints.reduce((total, point) => {
    const dx = anchor.x - point.x / width;
    const dy = anchor.y - point.y / height;
    return total + dx * dx + dy * dy;
  }, 0);
}

export function selectMeasurementHudCorner({
  imageWidth,
  imageHeight,
  bbox,
  pointer,
  topRightUnavailable,
}: MeasurementHudPlacementInput): MeasurementHudCorner {
  const width = Math.max(imageWidth, 1);
  const height = Math.max(imageHeight, 1);
  const bboxTouchesTopRight = Boolean(bbox && bbox.x + bbox.width > width * 0.65 && bbox.y < height * 0.35);
  const pointerTouchesTopRight = pointer.x > width * 0.65 && pointer.y < height * 0.35;
  if (!topRightUnavailable && !bboxTouchesTopRight && !pointerTouchesTopRight) return "top-right";

  const avoidPoints = [pointer];
  if (bbox) avoidPoints.push({ x: bbox.x + bbox.width / 2, y: bbox.y + bbox.height / 2 });
  const candidates = (Object.keys(CORNERS) as MeasurementHudCorner[]).filter((corner) => corner !== "top-right");

  return candidates.reduce((best, candidate) => {
    return scoreCorner(candidate, avoidPoints, width, height) > scoreCorner(best, avoidPoints, width, height)
      ? candidate
      : best;
  }, candidates[0]);
}
